import { 
  formatSiretDisplay, 
  formatDate, 
  formatDateTime, 
  formatRelationType, 
  escapeHtml 
} from '../utils/formatters';
import { ProspectItem, SentEmailNotification } from '../types';

export class ModalComponent {
  public static openRecapModal(
    prospect: ProspectItem, 
    isDuplicate: boolean, 
    duplicateMessage: string | null
  ): void {
    const modal = document.getElementById('recapModal');
    const alertBox = document.getElementById('duplicateAlertBox');
    const alertMessage = document.getElementById('duplicateAlertMessage');
    const grid = document.getElementById('recapDetailsGrid');

    if (!modal || !alertBox || !alertMessage || !grid) return;

    if (isDuplicate) {
      alertBox.style.display = 'flex';
      alertMessage.textContent = duplicateMessage || '';
    } else {
      alertBox.style.display = 'none';
    }

    grid.innerHTML = `
      <div class="recap-item">
        <div class="recap-label">SIRET Prospect</div>
        <div class="recap-val siret-code">${formatSiretDisplay(prospect.siret)}</div>
      </div>
      <div class="recap-item">
        <div class="recap-label">Date d'enregistrement</div>
        <div class="recap-val">${formatDate(prospect.registration_date)}</div>
      </div>
      <div class="recap-item">
        <div class="recap-label">Poste du contact</div>
        <div class="recap-val">${escapeHtml(prospect.contact_position)}</div>
      </div>
      <div class="recap-item">
        <div class="recap-label">Taille de l'entreprise</div>
        <div class="recap-val">${escapeHtml(prospect.company_size)}</div>
      </div>
      <div class="recap-item">
        <div class="recap-label">Type de relation</div>
        <div class="recap-val">
          <span class="relation-badge relation-${prospect.relation_type}">${formatRelationType(prospect.relation_type)}</span>
        </div>
      </div>
      <div class="recap-item">
        <div class="recap-label">Statut Réseau</div>
        <div class="recap-val">
          ${isDuplicate 
            ? '<span class="badge-tag badge-shared">⚠️ Déjà Référencé (Instruction Admin)</span>' 
            : '<span class="badge-tag badge-unique">✓ Nouveau Prospect Unique</span>'}
        </div>
      </div>
      <div class="recap-item recap-full">
        <div class="recap-label">Précision sur le lien saisie</div>
        <div class="relation-details-box">${escapeHtml(prospect.relation_details)}</div>
      </div>
    `;

    modal.style.display = 'flex';
  }

  public static closeRecapModal(): void {
    const modal = document.getElementById('recapModal');
    if (modal) modal.style.display = 'none';
  }

  public static openEmailModal(email: SentEmailNotification): void {
    const modal = document.getElementById('emailModal');
    if (!modal) return;

    const subjectEl = document.getElementById('emailModalSubject');
    const recipientEl = document.getElementById('emailModalRecipient');
    const dateEl = document.getElementById('emailModalDate');
    const siretEl = document.getElementById('emailModalSiret');
    const bodyEl = document.getElementById('emailModalBody');

    if (subjectEl) subjectEl.textContent = email.subject;
    if (recipientEl) recipientEl.textContent = `${email.recipient_name} (${email.recipient_email})`;
    if (dateEl) dateEl.textContent = formatDateTime(email.created_at);
    if (siretEl) siretEl.textContent = formatSiretDisplay(email.prospect_siret);
    if (bodyEl) bodyEl.textContent = email.body;

    modal.style.display = 'flex';
  }

  public static closeEmailModal(): void {
    const modal = document.getElementById('emailModal');
    if (modal) modal.style.display = 'none';
  }

  private static setInputValue(id: string, val: string): void {
    const el = document.getElementById(id) as HTMLInputElement | HTMLSelectElement | null;
    if (el) el.value = val;
  }

  private static setTextContent(id: string, text: string): void {
    const el = document.getElementById(id);
    if (el) el.textContent = text;
  }

  private static populateUserAccountForm(user?: import('../types').AdminUserItem): void {
    const pwdSection = document.getElementById('modalUserPasswordSection');
    const pwdInput = document.getElementById('modalUserPassword') as HTMLInputElement | null;

    if (user) {
      this.setTextContent('userAccountModalTitle', `Modifier le Compte : ${user.raison_sociale}`);
      this.setInputValue('userAccountEditId', String(user.id));
      this.setInputValue('modalUserSiret', formatSiretDisplay(user.siret));
      this.setTextContent('modalUserSiretCounter', `${user.siret.length} / 14`);
      this.setInputValue('modalUserRaisonSociale', user.raison_sociale);
      this.setInputValue('modalUserEmail', user.email);
      this.setInputValue('modalUserTelephone', user.telephone);
      this.setInputValue('modalUserRole', user.role);
      this.setInputValue('modalUserStatus', user.status || 'active');

      if (pwdSection) pwdSection.style.display = 'none';
      if (pwdInput) {
        pwdInput.removeAttribute('required');
        pwdInput.value = '';
      }
    } else {
      this.setTextContent('userAccountModalTitle', 'Nouveau Compte Utilisateur');
      this.setInputValue('userAccountEditId', '');
      this.setInputValue('modalUserSiret', '');
      this.setTextContent('modalUserSiretCounter', '0 / 14');
      this.setInputValue('modalUserRaisonSociale', '');
      this.setInputValue('modalUserEmail', '');
      this.setInputValue('modalUserTelephone', '');
      this.setInputValue('modalUserRole', 'apporteur');
      this.setInputValue('modalUserStatus', 'active');

      if (pwdSection) pwdSection.style.display = 'block';
      if (pwdInput) {
        pwdInput.setAttribute('required', 'required');
        pwdInput.value = '';
      }
    }
  }

  public static openUserAccountModal(user?: import('../types').AdminUserItem): void {
    const modal = document.getElementById('userAccountModal');
    if (!modal) return;
    this.populateUserAccountForm(user);
    modal.style.display = 'flex';
  }

  public static closeUserAccountModal(): void {
    const modal = document.getElementById('userAccountModal');
    if (modal) modal.style.display = 'none';
  }

  public static openResetPasswordModal(user: import('../types').AdminUserItem): void {
    const modal = document.getElementById('userPasswordModal');
    if (!modal) return;

    const idInput = document.getElementById('resetPasswordUserId') as HTMLInputElement | null;
    const targetEl = document.getElementById('resetPasswordUserTarget');
    const emailEl = document.getElementById('resetPasswordUserEmail');
    const pwdInput = document.getElementById('resetNewPassword') as HTMLInputElement | null;

    if (idInput) idInput.value = String(user.id);
    if (targetEl) targetEl.textContent = user.raison_sociale;
    if (emailEl) emailEl.textContent = user.email;
    if (pwdInput) pwdInput.value = '';

    modal.style.display = 'flex';
  }

  public static closeResetPasswordModal(): void {
    const modal = document.getElementById('userPasswordModal');
    if (modal) modal.style.display = 'none';
  }

  public static openDeleteUserModal(user: import('../types').AdminUserItem, isSelf: boolean): void {
    const modal = document.getElementById('userDeleteModal');
    if (!modal) return;

    const idInput = document.getElementById('deleteTargetUserId') as HTMLInputElement | null;
    const nameEl = document.getElementById('deleteUserTargetName');
    const siretEl = document.getElementById('deleteUserTargetSiret');
    const noticeEl = document.getElementById('deleteUserWarningNotice');
    const confirmBtn = document.getElementById('btnConfirmDeleteUser') as HTMLButtonElement | null;

    if (idInput) idInput.value = String(user.id);
    if (nameEl) nameEl.textContent = user.raison_sociale;
    if (siretEl) siretEl.textContent = formatSiretDisplay(user.siret);

    if (isSelf) {
      if (noticeEl) {
        noticeEl.innerHTML = '<span style="color:var(--danger); font-weight:bold;">Action impossible : vous ne pouvez pas supprimer votre propre compte administrateur actuellement connecté.</span>';
      }
      if (confirmBtn) confirmBtn.disabled = true;
    } else {
      if (noticeEl) {
        noticeEl.innerHTML = `Cette opération supprimera définitivement le compte ainsi que les <strong>${user.total_prospects}</strong> prospects déclarés par ce partenaire. Cette action est irréversible.`;
      }
      if (confirmBtn) confirmBtn.disabled = false;
    }

    modal.style.display = 'flex';
  }

  public static closeDeleteUserModal(): void {
    const modal = document.getElementById('userDeleteModal');
    if (modal) modal.style.display = 'none';
  }

  public static openMyAccountModal(user: import('../types').UserProfile): void {
    const modal = document.getElementById('myAccountModal');
    if (!modal) return;

    this.setInputValue('accountSiret', formatSiretDisplay(user.siret));
    this.setInputValue('accountRaisonSociale', user.raison_sociale);
    this.setInputValue('accountEmail', user.email);
    this.setInputValue('accountTelephone', user.telephone);

    const currentPwd = document.getElementById('accountCurrentPassword') as HTMLInputElement | null;
    const newPwd = document.getElementById('accountNewPassword') as HTMLInputElement | null;
    if (currentPwd) currentPwd.value = '';
    if (newPwd) newPwd.value = '';

    modal.style.display = 'flex';
  }

  public static closeMyAccountModal(): void {
    const modal = document.getElementById('myAccountModal');
    if (modal) modal.style.display = 'none';
  }
}
